import { useEffect, useState, useCallback, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequest } from '@stripe/stripe-js';
import { useAlert } from 'react-alert';
import { Checkout } from '../types/checkout';
import { Api } from './useApi';
import {
  MoedaVendaDescricao,
  MoedaVendaEnum,
} from '../types/enums/moeda-venda.enum';
import { getAllUrlParams, getUrlParams } from '../utils/get-url-params';
import { emitUtmifyEvent } from '../utils/emitUtmifyEvent';
import { MetodoPagamentoVendaEnum } from '../types/enums/metodo-pagamento.enum';
import { CreateCreditCardPuchaseContract } from '../types/create-credit-card-puchase-contract';
import { Sell } from '../types/venda';
import { IResponsePadrao } from '../types/response-padrao';
import { getConfig } from '../utils/get-config';

type Params = {
  checkout: Checkout;
  api: Api;
  gerarTokenRecaptcha: (path: string) => Promise<string>;
  onSuccess: (res: IResponsePadrao<Sell>) => void;
  supportsGooglePay: boolean;
};

type Result = {
  googlePayAvailable: boolean;
  handleGooglePayClick: () => void;
  isOpeningGooglePay: boolean;
};

export const useGooglePayExpress = ({
  checkout,
  api,
  gerarTokenRecaptcha,
  onSuccess,
  supportsGooglePay,
}: Params): Result => {
  const stripe = useStripe();
  const alert = useAlert();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  );
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  const [isOpeningGooglePay, setIsOpeningGooglePay] = useState(false);

  const apiRef = useRef(api);
  const gerarTokenRecaptchaRef = useRef(gerarTokenRecaptcha);
  const onSuccessRef = useRef(onSuccess);
  const alertRef = useRef(alert);
  const checkoutRef = useRef(checkout);

  useEffect(() => {
    apiRef.current = api;
    gerarTokenRecaptchaRef.current = gerarTokenRecaptcha;
    onSuccessRef.current = onSuccess;
    alertRef.current = alert;
    checkoutRef.current = checkout;
  }, [api, gerarTokenRecaptcha, onSuccess, alert, checkout]);

  const lastCheckoutIdRef = useRef<string | undefined>(undefined);
  const lastSubtotalRef = useRef<number | undefined>(undefined);
  const paymentRequestRef = useRef<PaymentRequest | null>(null);
  const resetOnLeaveRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!supportsGooglePay || !stripe) {
      setGooglePayAvailable(false);
      return;
    }

    const checkoutId = checkout?.id;
    const subtotal = checkout?.subtotal;

    if (
      lastCheckoutIdRef.current === checkoutId &&
      lastSubtotalRef.current === subtotal &&
      paymentRequestRef.current !== null
    ) {
      return;
    }

    if (!checkoutId || !subtotal || typeof window === 'undefined') {
      setGooglePayAvailable(false);
      return;
    }

    const currency =
      MoedaVendaDescricao[checkout.moeda ?? MoedaVendaEnum.BRL]?.toLowerCase();
    if (!currency) {
      setGooglePayAvailable(false);
      return;
    }

    const country = checkout.pais || 'US';
    const amountInCents = Math.round((checkout.subtotal ?? 0) * 100);

    let pr: PaymentRequest;
    try {
      pr = stripe.paymentRequest({
        country,
        currency,
        total: {
          label: checkout?.produto?.nome ?? 'Order',
          amount: amountInCents,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
        requestShipping: true,
      });
    } catch (e) {
      setGooglePayAvailable(false);
      return;
    }

    if (paymentRequestRef.current) {
      try {
        paymentRequestRef.current.off('paymentmethod');
        paymentRequestRef.current.off('shippingaddresschange');
        paymentRequestRef.current.off('shippingoptionchange');
      } catch (e) {
        console.warn('[GooglePay] Erro ao limpar listeners:', e);
      }
    }

    const forceWallets = getConfig('REACT_APP_SHOW_WALLETS') === 'true';
    if (forceWallets) {
      // DEV OVERRIDE: não deixa canMakePayment derrubar o estado no localhost
      setGooglePayAvailable(true);
      setPaymentRequest(pr);
      paymentRequestRef.current = pr;
      lastCheckoutIdRef.current = checkoutId;
      lastSubtotalRef.current = subtotal;
    } else {
      pr.canMakePayment()
        .then((result) => {
          const available = !!(result && (result.googlePay || result.wallet));
          setGooglePayAvailable(available);

          if (available) {
            setPaymentRequest(pr);
            paymentRequestRef.current = pr;
            lastCheckoutIdRef.current = checkoutId;
            lastSubtotalRef.current = subtotal;
          } else {
            setPaymentRequest(null);
            paymentRequestRef.current = null;
          }
        })
        .catch(() => {
          setGooglePayAvailable(false);
          setPaymentRequest(null);
          paymentRequestRef.current = null;
        });
    }

    pr.on('shippingaddresschange', (ev) => {
      const shippingOptions = [
        {
          id: 'standard',
          label: 'Entrega Padrão',
          detail: 'Entrega em 5-7 dias úteis',
          amount: 0,
        },
      ];

      ev.updateWith({
        status: 'success' as const,
        shippingOptions,
        total: {
          label: checkout?.produto?.nome ?? 'Order',
          amount: amountInCents,
        },
      });
    });

    pr.on('shippingoptionchange', (ev) => {
      ev.updateWith({
        status: 'success' as const,
        total: {
          label: checkout?.produto?.nome ?? 'Order',
          amount: amountInCents,
        },
      });
    });

    pr.on('paymentmethod', async (ev) => {
      const currentCheckout = checkoutRef.current;
      const currentApi = apiRef.current;
      const currentGerarToken = gerarTokenRecaptchaRef.current;
      const currentOnSuccess = onSuccessRef.current;
      const currentAlert = alertRef.current;

      if (!currentCheckout?.id) {
        ev.complete('fail');
        resetOnLeaveRef.current?.();
        resetOnLeaveRef.current = null;
        setIsOpeningGooglePay(false);
        return;
      }

      try {
        const recaptchaToken = await currentGerarToken('/venda');
        const afiliadoId = getUrlParams('affiliate') ?? undefined;
        const shipping = ev.shippingAddress as any;
        const billing = ev.paymentMethod?.billing_details?.address;

        // Google Pay usa address1/address2/address3 em vez de addressLine[]
        const addressLine =
          shipping?.addressLine?.[0] ||
          shipping?.address1 ||
          billing?.line1 ||
          '';
        const complementLine =
          shipping?.addressLine?.[1] ||
          shipping?.address2 ||
          billing?.line2 ||
          '';

        const payload: CreateCreditCardPuchaseContract = {
          metodoPagamento: MetodoPagamentoVendaEnum.GOOGLE_PAY,
          produtoId: currentCheckout.produtoId,
          checkoutId: currentCheckout.id,
          recaptchaToken,
          afiliadoId,
          emailComprador: ev.payerEmail ?? '',
          nomeComprador: ev.payerName ?? '',
          sobrenomeComprador: ev.payerName ?? '',
          telefoneComprador: (ev.payerPhone ?? '').replace(/\D/g, ''),
          postalCode:
            shipping?.postalCode ?? shipping?.postal_code ?? billing?.postal_code ?? undefined,
          state: shipping?.region ?? shipping?.administrativeArea ?? billing?.state ?? undefined,
          city: shipping?.city ?? shipping?.locality ?? billing?.city ?? undefined,
          district: shipping?.dependentLocality || '-',
          address: addressLine,
          number: addressLine.match(/\d+$/)?.[0] || '-',
          complement: complementLine,
          token: ev.paymentMethod.id,
          installments: 1,
          cpfComprador: '',
          utmSource: getUrlParams('utm_source'),
          utmCampaign: getUrlParams('utm_campaign'),
          utmMedium: getUrlParams('utm_medium'),
          utmContent: getUrlParams('utm_content'),
          utmTerm: getUrlParams('utm_term'),
          urlParams: getAllUrlParams(),
        };

        const res = await currentApi.Venda.createCC(payload);

        if (res.error) {
          ev.complete('fail');
          resetOnLeaveRef.current?.();
          resetOnLeaveRef.current = null;
          setIsOpeningGooglePay(false);
          const errorMessage =
            res.message?.[0] ??
            'Não foi possível concluir o pagamento com Google Pay.';
          currentAlert.show(errorMessage);
          return;
        }

        emitUtmifyEvent({
          event: 'waiting_payment',
          vendaId: res.data?.id,
        });
        ev.complete('success');
        resetOnLeaveRef.current?.();
        resetOnLeaveRef.current = null;
        setIsOpeningGooglePay(false);
        currentOnSuccess(res);
      } catch (err: any) {
        console.error('[GooglePay] Erro no handler paymentmethod:', err);
        ev.complete('fail');
        resetOnLeaveRef.current?.();
        resetOnLeaveRef.current = null;
        setIsOpeningGooglePay(false);
        const errorMessage =
          err?.message ||
          'Erro inesperado ao processar Google Pay. Tente novamente.';
        currentAlert.show(errorMessage);
      }
    });

    return () => {
      if (pr) {
        try {
          pr.off('paymentmethod');
          pr.off('shippingaddresschange');
          pr.off('shippingoptionchange');
        } catch (e) {
          console.warn('[GooglePay] Erro ao remover listeners:', e);
        }
      }
    };
  }, [
    stripe,
    supportsGooglePay,
    checkout?.id,
    checkout?.subtotal,
    checkout?.moeda,
    checkout?.pais,
    checkout?.produto?.nome,
  ]);

  const handleGooglePayClick = useCallback(() => {
    if (!paymentRequest || !googlePayAvailable) return;

    const release = (debounceMs = 0) => {
      const doRelease = () => {
        resetOnLeaveRef.current?.();
        resetOnLeaveRef.current = null;
        setIsOpeningGooglePay(false);
      };
      debounceMs ? setTimeout(doRelease, debounceMs) : doRelease();
    };

    try {
      setIsOpeningGooglePay(true);
      const onLeave = () => release(300);
      const onVisible = () =>
        document.visibilityState === 'visible' && onLeave();
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', onLeave);
      resetOnLeaveRef.current = () => {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onLeave);
      };

      const showPromise = paymentRequest.show() as unknown as
        | Promise<unknown>
        | undefined;
      showPromise?.then(() => release())?.catch(() => release());
    } catch {
      release();
    }
  }, [paymentRequest, googlePayAvailable]);

  return { googlePayAvailable, handleGooglePayClick, isOpeningGooglePay };
};
